import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  CircularProgress,
} from "@mui/material";
import { theme } from "../../theme/colors";

const REPORT_REASONS = [
  { key: "harassment", label: "Harassment or bullying" },
  { key: "hate_speech", label: "Hate speech" },
  { key: "spam", label: "Spam or misleading" },
  { key: "violence", label: "Violence or threats" },
  { key: "doxxing", label: "Sharing private information" },
  { key: "off_topic", label: "Not civic related" },
  { key: "other", label: "Other" },
];

/**
 * ReportModal – reason picker + optional details, matches mobile ReportModal.
 * Props: open, contentType ("post" | "comment"), onClose, onSubmit(reason, details)
 */
export default function ReportModal({ open, contentType = "post", onClose, onSubmit }) {
  const [selectedReason, setSelectedReason] = useState("");
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setSelectedReason("");
      setDetails("");
      setSubmitting(false);
    }
  }, [open]);

  const handleClose = () => {
    if (!submitting) {
      setSelectedReason("");
      setDetails("");
      onClose();
    }
  };

  const handleSubmit = async () => {
    if (!selectedReason || submitting) return;
    setSubmitting(true);
    try {
      await onSubmit(selectedReason, details.trim());
      setSelectedReason("");
      setDetails("");
      onClose();
    } catch {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="report-modal-title"
      BackdropProps={{
        sx: { backgroundColor: "rgba(0,0,0,0.35)", backdropFilter: "blur(2px)" },
      }}
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
        },
      }}
    >
      <DialogTitle id="report-modal-title" sx={{ fontWeight: 700, color: "#111827", pb: 0 }}>
        Report {contentType}
      </DialogTitle>
      <DialogContent sx={{ pt: 1, pb: 0 }}>
        <Typography variant="body2" sx={{ color: "#111827", mb: 2 }}>
          Why are you reporting this {contentType}?
        </Typography>
        <RadioGroup
          value={selectedReason}
          onChange={(e) => setSelectedReason(e.target.value)}
          sx={{ mb: 2 }}
        >
          {REPORT_REASONS.map((r) => (
            <FormControlLabel
              key={r.key}
              value={r.key}
              control={<Radio size="small" sx={{ color: "#64748b", "&.Mui-checked": { color: theme.primary } }} />}
              label={<Typography sx={{ fontSize: "15px", color: "#111827" }}>{r.label}</Typography>}
              sx={{ m: 0, mb: 0.5 }}
            />
          ))}
        </RadioGroup>
        <TextField
          fullWidth
          multiline
          minRows={3}
          maxRows={5}
          placeholder="Add any additional context"
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          inputProps={{ maxLength: 500 }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              bgcolor: "#fff",
              "& fieldset": { borderColor: "#e2e8f0" },
            },
          }}
        />
        {details.length > 0 && (
          <Typography variant="caption" sx={{ color: "#64748b", mt: 0.5, display: "block" }}>
            {details.length}/500
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={submitting}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "10px",
            bgcolor: "#fff",
            color: "#111827",
            border: "1px solid #111827",
            "&:hover": {
              bgcolor: "#f8fafc",
              border: "1px solid #111827",
              color: "#111827",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!selectedReason || submitting}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "10px",
            bgcolor: "#dc2626",
            color: "#fff",
            "&:hover": { bgcolor: "#b91c1c", color: "#fff" },
            "&.Mui-disabled": { bgcolor: "#e5e7eb", color: "#9ca3af" },
          }}
        >
          {submitting ? <CircularProgress size={22} color="inherit" /> : "Submit Report"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
